import { Dices } from "lucide-react";
import { useState } from "react";
import type { DiceRollVisibility } from "../state/type-registry";
import { IconButton } from "./icon-button";

type DiceRollerProps = {
  onRoll: (expression: string, visibility: DiceRollVisibility) => void;
  visibility: DiceRollVisibility;
  onVisibilityChange: (visibility: DiceRollVisibility) => void;
  onError: (message: string) => void;
};

const QUICK_ROLL_EXPRESSIONS = ["d4", "d6", "d8", "d10", "d12", "d20", "d100"];

export function DiceRoller({
  onRoll,
  visibility,
  onVisibilityChange,
  onError,
}: DiceRollerProps) {
  const [expression, setExpression] = useState("");

  const submit = () => {
    const normalized = expression.trim();
    if (!normalized) {
      onError("Enter a dice expression first.");
      return;
    }
    onRoll(normalized, visibility);
    setExpression("");
    onError("");
  };

  return (
    <div className="rounded border border-white/10 bg-black/20 p-1.5 space-y-1 mb-2">
      <div className="flex flex-wrap items-center gap-1">
        {QUICK_ROLL_EXPRESSIONS.map((quickRoll) => (
          <IconButton
            key={quickRoll}
            tooltip={`Roll ${quickRoll}`}
            className="h-6 px-2 text-[10px] text-white/65 border border-white/10 rounded bg-white/[0.04] hover:bg-white/8"
            onClick={() => setExpression(quickRoll)}
          >
            {quickRoll}
          </IconButton>
        ))}

        <button
          onClick={() => setExpression("")}
          className="ml-auto text-[10px] px-2 h-6 rounded border border-white/10 text-white/50 hover:text-white/75 hover:bg-white/8"
        >
          Clear
        </button>
      </div>

      <div className="flex items-center gap-1">
        <div className="w-20 text-[10px] text-white/55">Visibility</div>
        <select
          value={visibility}
          onChange={(event) =>
            onVisibilityChange(event.target.value as DiceRollVisibility)
          }
          className="h-6 px-1 rounded border border-white/10 bg-white/5 text-[10px] text-white/80"
        >
          <option value="public">Public</option>
          <option value="gm-only">GM-only</option>
        </select>
      </div>

      <div className="flex items-center gap-1">
        <input
          value={expression}
          onChange={(event) => setExpression(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder="Roll expression, e.g. 2d20+3"
          className="flex-1 h-6 rounded bg-white/5 border border-white/10 text-[11px] text-white/85 px-2"
        />

        <IconButton
          tooltip="Roll"
          className="w-7 h-6 rounded bg-white/10 border border-white/15 text-white/75 hover:text-white/95"
          onClick={submit}
        >
          <Dices className="w-4 h-4" />
        </IconButton>
      </div>
    </div>
  );
}
